// Firebase configuration and initialization
import { initializeApp, getApps } from 'firebase/app';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID
};

// Check if Firebase is configured
const isFirebaseConfigured = () => {
  return firebaseConfig.projectId && firebaseConfig.apiKey && firebaseConfig.appId;
};

// Initialize Firebase (singleton pattern)
let app = null;
let messaging = null;

export function getFirebaseApp() {
  if (!isFirebaseConfigured()) {
    console.log('Firebase not configured, skipping initialization');
    return null;
  }
  if (!app && !getApps().length) {
    app = initializeApp(firebaseConfig);
  }
  return app || getApps()[0];
}

export function getFirebaseMessaging() {
  if (!isFirebaseConfigured()) {
    return null;
  }
  if (!messaging) {
    const app = getFirebaseApp();
    if (!app) return null;
    messaging = getMessaging(app);
  }
  return messaging;
}

// Request notification permission and get FCM token
export async function requestNotificationPermission() {
  if (!isFirebaseConfigured()) {
    console.log('Firebase not configured, skipping notification permission');
    return null;
  }
  
  try {
    const permission = await Notification.requestPermission();
    
    if (permission === 'granted') {
      const messaging = getFirebaseMessaging();
      if (!messaging) return null;
      
      const token = await getToken(messaging, {
        vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY
      });
      
      console.log('FCM Token:', token);
      return token;
    } else {
      console.log('Notification permission denied');
      return null;
    }
  } catch (error) {
    console.error('Error getting notification permission:', error);
    return null;
  }
}

// Register token with backend
export async function registerTokenWithBackend(token) {
  try {
    const authToken = localStorage.getItem('token');
    if (!authToken) {
      console.log('No auth token, skipping FCM registration');
      return false;
    }

    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/notifications/register-token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`
      },
      body: JSON.stringify({
        token: token,
        device_type: 'web'
      })
    });

    if (response.ok) {
      console.log('FCM token registered with backend');
      return true;
    } else {
      console.error('Failed to register token with backend');
      return false;
    }
  } catch (error) {
    console.error('Error registering token:', error);
    return false;
  }
}

// Unregister token when user logs out
export async function unregisterToken() {
  try {
    const authToken = localStorage.getItem('token');
    if (!authToken) return;

    await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/notifications/unregister-token`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${authToken}`
      }
    });
  } catch (error) {
    console.error('Error unregistering token:', error);
  }
}

// Listen for foreground messages
export function onForegroundMessage(callback) {
  if (!isFirebaseConfigured()) {
    // Return empty unsubscribe function
    return () => {};
  }
  
  const messaging = getFirebaseMessaging();
  if (!messaging) return () => {};
  
  return onMessage(messaging, (payload) => {
    console.log('Foreground message received:', payload);
    callback(payload);
  });
}

// Initialize notifications - call this after user logs in
export async function initializeNotifications() {
  // Check if Firebase is configured
  if (!isFirebaseConfigured()) {
    console.log('Firebase not configured, notifications disabled');
    return false;
  }
  
  // Check if browser supports notifications
  if (!('Notification' in window)) {
    console.log('This browser does not support notifications');
    return false;
  }

  // Check if service worker is supported
  if (!('serviceWorker' in navigator)) {
    console.log('Service workers not supported');
    return false;
  }

  try {
    // Register service worker
    const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
    console.log('Service Worker registered:', registration);

    // Request permission and get token
    const token = await requestNotificationPermission();
    
    if (token) {
      // Register token with backend
      await registerTokenWithBackend(token);
      return true;
    }
    
    return false;
  } catch (error) {
    console.error('Error initializing notifications:', error);
    return false;
  }
}
